import React from 'react'
import styled, { useTheme } from 'styled-components'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { graphql, navigate } from 'gatsby'
import { useMedia } from 'react-use'

import { AuthorChip } from 'components/AuthorChip'
import { EventChip } from 'components/EventChip'
import { Box } from 'components/Box'
import { Brand } from 'components/Brand'
import { Card } from 'components/Card'
import { Heading, Text, TextGroup } from 'components/Text'
import { Footer } from 'components/Footer'
import Seo from 'components/Seo'

const Root = styled(Box)`
  min-height: 100vh;
  display: grid;
  grid-template-rows: 0.333fr 1fr min-content;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    grid-template-rows: 0.5fr 1fr min-content;
  }
`

const Hero = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Grid = styled(Box)`
  max-width: ${({ children, theme }) => theme.breakpoints[children.length]};
  margin: 0 auto;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-auto-rows: 1fr;
  padding-left: ${({ theme }) => theme.space[6]};
  padding-right: ${({ theme }) => theme.space[6]};
  grid-gap: ${({ theme }) => theme.space[6]};

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    grid-template-columns: repeat(
      ${({ children }) => Math.min(children.length, 3)},
      1fr
    );
  }
`

const TutorialCard = styled(Card)`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  overflow: hidden;
  max-height: 20rem;
`

const FeaturedImage = styled(GatsbyImage)`
  max-height: 11rem;
`

const Chips = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Index = (props) => {
  const { tutorials } = props.data
  const theme = useTheme()
  const isSm = useMedia(`(min-width: ${theme.breakpoints[1]})`)
  return (
    <Root>
      <Seo />
      <Hero paddingX={4} minHeight={12}>
        <Brand size={isSm ? 2 : 1.5} />
        <Text
          textAlign="center"
          marginY={4}
          fontSize={4}
          lineHeight={6}
          fontWeight="600"
        >
          Learn to code with Stream, through interactive tutorials.
        </Text>
      </Hero>
      <Grid>
        {tutorials.edges.map(({ node }) => (
          <TutorialCard
            onClick={() => navigate(node.uri)}
            boxShadow={1}
            borderRadius={4}
          >
            <FeaturedImage
              image={getImage(
                node.childMdx.frontmatter.featuredImage ||
                  node.childMdx.frontmatter.ogImage
              )}
            />
            <TextGroup marginTop={4} paddingX={6}>
              <Heading fontSize={5} lineHeight={6} fontWeight="800">
                {node.title}
              </Heading>
              <Text
                marginY={3}
                fontSize={3}
                fontWeight="400"
                color="altText"
                lineHeight={5}
              >
                {node.description}
              </Text>
            </TextGroup>
            <Chips paddingY={4} paddingX={6}>
              <AuthorChip avatar={node.author.avatar} name={node.author.name} />
              <EventChip
                color={node.event.color}
                logo={node.event.childMdx.frontmatter.logo}
                name={node.event.name}
                url={node.event.url}
              />
            </Chips>
          </TutorialCard>
        ))}
      </Grid>
      <Footer />
    </Root>
  )
}

export const pageQuery = graphql`
  fragment TutorialPreview on Tutorial {
    title
    description
    uri
    author {
      name
      avatar
    }
    event {
      name
      childMdx {
        frontmatter {
          logo {
            childImageSharp {
              gatsbyImageData(width: 32)
            }
          }
        }
      }
      color
      uri
    }
    language {
      name
      color
      icon
      uri
    }
    childMdx {
      frontmatter {
        ogImage {
          childImageSharp {
            gatsbyImageData
          }
        }
        featuredImage {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
  query getHomepage {
    tutorials: allTutorial {
      edges {
        node {
          ...TutorialPreview
        }
      }
    }

    byEvent: allTutorial {
      group(field: event___name) {
        eventName: fieldValue
        nodes {
          ...TutorialPreview
        }
      }
    }

    byAuthor: allTutorial {
      group(field: author___name) {
        authorName: fieldValue
        nodes {
          ...TutorialPreview
        }
      }
    }

    byLanguage: allTutorial {
      group(field: language___name) {
        languageName: fieldValue
        nodes {
          ...TutorialPreview
        }
      }
    }
  }
`

export default Index
